import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      toast.error("Payment verification timed out. Please contact support if credits were not added.");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === "completed" || response.data.status === "paid") {
        setStatus("success");
        setCreditsAdded(response.data.credits_added);
        await refreshUser();
        toast.success("Payment successful!");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="card-base max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-[#4F46E5] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verifying Payment...
              </h2>
              <p className="text-slate-600">Please wait while we confirm your payment.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Successful!
              </h2>
              <p className="text-slate-600 mb-6">
                {creditsAdded > 0 
                  ? `${creditsAdded} credits have been added to your account.`
                  : "Your credits have been added to your account."}
              </p>
              <Button 
                onClick={() => navigate("/dashboard")}
                className="btn-primary w-full"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Verification Failed
              </h2>
              <p className="text-slate-600 mb-6">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  className="flex-1"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="flex-1 btn-primary"
                >
                  Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
